import {
	Header,
	Sidebar,
	SideDrawerWrapper,
	ReviewPopup,
	TutorialProvider,
	TutorialTour,
} from 'components/Global';
import { FORMDATA } from 'constants/Data';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Outlet,
	useLocation,
	useNavigate,
	useSearchParams,
	// useSearchParams,
} from 'react-router-dom';
import { checkPermission, hasText } from 'utils/utils';
import styles from './index.module.scss';
import { fetchClosingDateAction } from 'redux/action/closingDateAction';
import { AppDispatch } from 'redux/store';
import useIdleRedirect from 'utils/logoutTimer';
import useAiAssistantAccess from 'utils/useAiAssistantAccess';
import axios from 'axios';
import { loginAction } from 'redux/slice/loginSlice';
import { fetchProfileAction, updateReviewStatusAction } from 'redux/action/profileAction';
import { getCompanies } from 'redux/slice/companySlice';
import { setSidebarCollapsed } from 'redux/slice/layoutExpandSlice';
import { Spin } from 'antd';
import { LoadingOutlined, SettingOutlined } from '@ant-design/icons';
import { showNotification } from 'utils/notification';
import SettingsLayout from 'layouts/Settings';
import { SettingsBody } from 'components/settings';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

// Creating the global layout for along all the pages
const GlobalLayout = () => {
	// inits
	const { pageMenuItems } = FORMDATA;
	const { data: listOfCompanies } = useSelector(
		(state: any) => state?.companies
	);
	const { isLoading, data: userData } = useSelector(
		(state: any) => state.userProfile
	);
	const { pathname } = useLocation();
	const [finalMenuItems, setMenuItems] = useState<any>([]);

	const [isLoggedIn, setIsLoggedIn] = useState(false);

	const dispatch = useDispatch<AppDispatch>();
	const { settingsMenuItems } = FORMDATA;
	const [drawerAnimation, setDrawerAnimation] = useState<boolean>(false);
	const [isSideDrawerOpen, setSideDrawerOpen] = useState<boolean>(false);
	const [filteredMenuItems, setFilteredMenuItems] = useState<any>([]);
	const [selectedSidebarSettings, setSelectedSidebarSettings] = useState<any>(
		localStorage.getItem('settings')
	);
	const [selectedSidebar, setSelectedSidebar] = useState<string>('dashboard');
	const [searchParams, setSearchParams] = useSearchParams();
	const [profileData, setProfileData] = useState(null);
	const openDrawerFlag = searchParams.get('openDrawer');
	const drawerSelection = searchParams.get('drawerSelection');
	const activeKey = searchParams.get('activeKey');
	const [drawerInfo] = useState({ drawerTitle: 'Settings' });
	const [isReviewPopupOpen, setIsReviewPopupOpen] = useState(false);
	const [isReviewActionLoading, setIsReviewActionLoading] = useState(false);

	const { isExpanded, sidebarCollapsed } = useSelector(
		(state: any) => state?.layoutExpand ?? {}
	);

	const navigate = useNavigate();

	// The AI assistant is behind a frontend email allow list for now —
	// see constants/aiAccess.
	const hasAiAssistantAccess = useAiAssistantAccess();

	// const [queryParams] = useSearchParams();

	const { selectedCompanyDetails } = useSelector(
		(state: any) => state?.companies
	);
	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);

	// Check if reauthorization is required
	const requiresReauthorization =
		selectedCompanyDetails?.company?.requiresReauthorization === true;
	const isCompanyAdmin =
		selectedCompanyDetails?.role?.isCompanyAdmin === true;

	const [hasShownNotification, setHasShownNotification] = useState(false);

	// Show notification when reauthorization is required
	useEffect(() => {
		if (
			requiresReauthorization &&
			isCompanyAdmin &&
			!hasShownNotification &&
			selectedCompanyDetails?.company?.id
		) {
			showNotification({
				type: 'warning',
				message: 'Reauthorization Required',
				description:
					'Company Admin role has been transferred to you. Please reauthorize your QuickBooks connection to continue using all features.',
				duration: 0, // Don't auto-close, only manual close
				placement: 'topRight',
				key: 'quickbooks-reauthorization-required',
			});
			setHasShownNotification(true);
		}
	}, [requiresReauthorization, isCompanyAdmin, hasShownNotification, selectedCompanyDetails?.company?.id]);

	// Reset notification state when company changes
	useEffect(() => {
		setHasShownNotification(false);
	}, [selectedCompanyDetails?.company?.id]);

	const isDashboardPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Dashboard',
		permission: ['view'],
	});

	const isEmployeePermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Employee Cost',
		permission: ['view'],
	});

	const isTimeLogPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Time Logs',
		permission: ['view'],
	});

	const isTimeSheetPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Time Sheets',
		permission: ['view'],
	});

	const isCostAllocationPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Cost Allocations',
			permission: ['view'],
		}
	);

	const isJournalEntriesPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Journals Entries',
			permission: ['view'],
		}
	);

	const isTimeSummaryPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Time Summary',
		permission: ['view'],
	});

	const isPayrollSummaryPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Payroll Summary',
			permission: ['view'],
		}
	);

	const isCustomerSummaryPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Customer Overview',
			permission: ['view'],
		}
	);

	// Expense Allocation permissions
	const isExpenseTransactionPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Expense Transactions',
		permission: ['view'],
	});


	const isExpenseJournalPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Expense Journal',
		permission: ['view'],
	});

	// const isPayPeriodsPermission = checkPermission(selectedCompanyPermission, {
	// 	permissionName: 'Pay Period',
	// 	permission: ['view'],
	// });


	const openDrawerHandler = () => {
		setDrawerAnimation(true);
		setSideDrawerOpen(true);
	};

	useEffect(() => {
		if (
			listOfCompanies &&
			!listOfCompanies?.length &&
			userData.isFirstCompanyAdmin
		) {
			openDrawerHandler();
		}
	}, [listOfCompanies]);

	const closeDrawerByAnimation = () => {
		setDrawerAnimation(false);
		filterSettingsMenuHandler();
		// localStorage.removeItem('settings');

		// Remove drawer-related URL parameters only when drawerSelection is "Expense Groups"
		if (drawerSelection === 'Expense Groups') {
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete('openDrawer');
			newSearchParams.delete('drawerSelection');
			navigate(`${window.location.pathname}?${newSearchParams.toString()}`, {
				replace: true,
			});
		}
	};

	const removeDrawerFromDom = () => {
		setSideDrawerOpen(false);
	};


	const settingsSidebarHandler = () => {
		openDrawerHandler();
		const currentsettings = localStorage.getItem('settings');
		if (currentsettings) {
			setSelectedSidebarSettings(currentsettings);
			return;
		} else if (isAnyUserPermission) {
			localStorage.setItem('settings', 'Users');
			setSelectedSidebarSettings('Users');
		} else if (isAnyRolePermission) {
			localStorage.setItem('settings', 'Roles');
			setSelectedSidebarSettings('Roles');
		} else if (isAnyIntegrationPermission) {
			localStorage.setItem('settings', 'Integrations');
			setSelectedSidebarSettings('Integrations');
		} else if (isAnySubscriptionPermission) {
			localStorage.setItem('settings', 'Subscription');
			setSelectedSidebarSettings('Subscription');
		} else if (isAnyConfigurationPermission) {
			localStorage.setItem('settings', 'Configurations');
			setSelectedSidebarSettings('Configurations');
		} else if (isAnySyncLogsPermission) {
			localStorage.setItem('settings', 'Sync Logs');
			setSelectedSidebarSettings('Sync Logs');
		} else if (isAnyCustomRulePermission) {
			localStorage.setItem('settings', 'CustomRules');
			setSelectedSidebarSettings('CustomRules');
		} else if (isAnyExpenseGroupsPermission) {
			localStorage.setItem('settings', 'Expense Groups');
			setSelectedSidebarSettings('Expense Groups');
		} else if (isAnyEmployeesPermission) {
			localStorage.setItem('settings', 'Employees');
			setSelectedSidebarSettings('Employees');
		}
	};

	const sideBarChangeHandler = (selectedValue: any) => {
		localStorage.setItem('settings', selectedValue?.key);
		setSelectedSidebarSettings(selectedValue?.key);
	};

	const filterSettingsMenuHandler = () => {
		const filteredPermissionsState = settingsMenuItems.map(
			(singleMenuItem: any) => {
				return checkPermission(selectedCompanyPermission, {
					permissionName: singleMenuItem?.key,
					permission: ['view'],
				});
			}
		);

		if (!selectedCompanyDetails?.length) {
			filteredPermissionsState[2] = true;
		}

		const filteredMenuItems = settingsMenuItems.filter(
			(_, i) => filteredPermissionsState[i]
		);
		setFilteredMenuItems(filteredMenuItems);
		setSelectedSidebarSettings(filteredMenuItems[0]?.key);
	};
	const isAnyUserPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Users',
		permission: ['add', 'all', 'edit', 'view', 'delete'],
	});
	const isAnyRolePermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Roles',
		permission: ['add', 'all', 'edit', 'view', 'delete'],
	});

	const isAnyIntegrationPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Integrations',
			permission: ['add', 'all', 'edit', 'view', 'delete'],
		}
	);

	const isAnySubscriptionPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Subscriptions',
			permission: ['add', 'all', 'edit', 'view', 'delete'],
		}
	);

	const isAnyConfigurationPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Configurations',
			permission: ['add', 'all', 'edit', 'view', 'delete'],
		}
	);

	const isAnySyncLogsPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Sync Logs',
		permission: ['add', 'all', 'edit', 'view', 'delete'],
	});

	const isAnyCustomRulePermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Custom Rules',
		permission: ['add', 'all', 'edit', 'view', 'delete'],
	});

	const isAnyExpenseGroupsPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Expense Groups',
		permission: ['add', 'all', 'edit', 'view', 'delete'],
	});

	const isAnyEmployeesPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Employees',
		permission: ['add', 'all', 'edit', 'view', 'delete'],
	});

	useEffect(() => {
		if (openDrawerFlag === 'true') {
			if (hasText(drawerSelection)) {
				localStorage.setItem('settings', drawerSelection as string);
				setSelectedSidebarSettings(drawerSelection);
			}
			openDrawerHandler();
		}
		if (activeKey) {
			localStorage.setItem('settings', activeKey);
			setSelectedSidebarSettings(activeKey);
		}
	}, [openDrawerFlag, drawerSelection, activeKey]);

	useEffect(() => {
		filterSettingsMenuHandler();
	}, [selectedCompanyPermission, selectedCompanyDetails]);

	useEffect(() => {
		let menuItems = pageMenuItems;
		if (!isDashboardPermission) {
			menuItems = menuItems?.filter((item) => item?.key !== 'dashboard');
		}

		// if (!isEmployeePermission && !isPayPeriodsPermission) {
		// 	menuItems = menuItems?.filter((item) => item?.key !== 'employee-costs');
		// }

		// Handle nested payroll-allocation menu
		menuItems = menuItems?.map((item: any) => {
			if (item?.key === 'payroll-allocations' && item?.children) {
				const filteredChildren = item.children.filter((child: any) => {
					if (child?.key === 'time-activity' && !isTimeLogPermission && !isTimeSheetPermission) {
						return false;
					}
					if (child?.key === 'cost-allocations' && !isCostAllocationPermission && !isJournalEntriesPermission) {
						return false;
					}
					return true;
				});
				return { ...item, children: filteredChildren };
			}
			return item;
		});

		// Filter out payroll-allocation if no children remain
		menuItems = menuItems?.filter((item: any) => {
			if (item?.key === 'payroll-allocation') {
				return item?.children && item.children.length > 0;
			}
			return true;
		});

		if (!isJournalEntriesPermission) {
			menuItems = menuItems?.filter((item) => item?.key !== 'journal-entries');
		}

		// Filter expense allocation menu based on any child permission
		if (!isExpenseTransactionPermission && !isExpenseJournalPermission) {
			menuItems = menuItems?.filter((item) => item?.key !== 'expense-allocations');
		}

		if (
			!isTimeSummaryPermission &&
			!isPayrollSummaryPermission &&
			!isCustomerSummaryPermission
		) {
			menuItems = menuItems?.filter((item) => item?.key !== 'reports');
		}

		const hasSettingsPermission = settingsMenuItems.some((item: any) =>
			checkPermission(selectedCompanyPermission, {
				permissionName: item?.key,
				permission: ['view'],
			})
		);
		if (hasSettingsPermission) {
			menuItems = [
				...menuItems,
				{
					key: 'settings',
					label: 'Settings',
					icon: <SettingOutlined />,
				},
			];
		}

		setMenuItems(menuItems);
	}, [
		isEmployeePermission,
		isTimeLogPermission,
		isDashboardPermission,
		isCostAllocationPermission,
		isTimeSheetPermission,
		isJournalEntriesPermission,
		isExpenseTransactionPermission,
		isExpenseJournalPermission,
		pageMenuItems,
		selectedCompanyPermission,
		selectedCompanyDetails,
	]);

	const handleSidebar = (sidebarProps: any) => {
		if (sidebarProps?.key === 'settings') {
			settingsSidebarHandler();
			// Do NOT change selectedSidebar or route
			return;
		}
		// Parent menu - don't navigate
		if (sidebarProps?.key === 'payroll-allocation') {
			return;
		}
		if (sidebarProps?.key === 'employee-costs') {
			setSelectedSidebar('employee-costs');
			navigate('/employee-costs');
		} else if (sidebarProps?.key === 'time-activity') {
			setSelectedSidebar('time-activity');
			navigate('/time-activity');
		} else if (sidebarProps?.key === 'cost-allocations') {
			setSelectedSidebar('cost-allocations');
			navigate('/cost-allocations');
		} else if (sidebarProps?.key === 'expense-allocations') {
			setSelectedSidebar('expense-allocations');
			navigate('/expense-allocations');
		} else if (sidebarProps?.key === 'journal-entries') {
			setSelectedSidebar('journal-entries');
			navigate('/journal-entries');
		} else if (sidebarProps?.key === 'reports') {
			setSelectedSidebar('reports');
			navigate('/reports');
		} else {
			setSelectedSidebar('dashboard');
			navigate('/dashboard');
		}
	};
	const initialFunctionCall = () => {
		if (pathname === '/employee-costs') {
			setSelectedSidebar('employee-costs');
		} else if (pathname === '/time-activity') {
			setSelectedSidebar('time-activity');
		} else if (pathname === '/cost-allocations') {
			setSelectedSidebar('cost-allocations');
		} else if (pathname === '/expense-allocations') {
			setSelectedSidebar('expense-allocations');
		} else if (pathname === '/journal-entries') {
			setSelectedSidebar('journal-entries');
		} else if (pathname === '/reports') {
			setSelectedSidebar('reports');
		} else if (pathname === '/chat') {
			// The assistant is not one of the page-menu entries, so nothing in
			// that menu should look active while it is open.
			setSelectedSidebar('chat');
		} else {
			setSelectedSidebar('dashboard');
		}
	};

	useEffect(() => {
		initialFunctionCall();
	}, [pathname]);

	useEffect(() => {
		// Remove access_token from the URL after extracting it for security purposes
		if (searchParams.get('access_token')) {
			const accessToken = searchParams.get('access_token');
			if (accessToken) {
				// When arriving with a fresh ?access_token= in the URL,
				// clear any stale Outseta state before writing the new token.
				// This mirrors OutsetaLogin's CAP-10 clean-up and fixes the
				// white screen + red toast seen after Outseta-hosted password reset.
				localStorage.removeItem('outsetaAccessToken');
				Object.keys(sessionStorage)
					.filter(key => key.startsWith('outseta'))
					.forEach(key => sessionStorage.removeItem(key));
				try { (window as any).Outseta?.setAccessToken(null); } catch (e) { /* ignore */ }

				localStorage.setItem('outsetaAccessToken', accessToken);
				sessionStorage.setItem(
					'Outseta.nocode.accessToken',
					localStorage.getItem('outsetaAccessToken')!
				); // Store in localStorage
			}
			setSearchParams({}); // Clear the search params (access_token)
		}
	}, []);

	// useEffect(() => {

	// }, [profileData]);
	const [isOutsetaLoading, setIsOutsetaLoading] = useState(false);

	useEffect(() => {
		const fetchProfileData = async () => {
			const outsetaToken = localStorage.getItem('outsetaAccessToken');
			const token = localStorage.getItem('accessToken');

			if (!outsetaToken) {
				navigate('/login');
				return;
			}

			if (!outsetaToken || !token) {
				try {
					localStorage.setItem('outsetaLoginStart', 'true'); // Store in localStorage
					setIsOutsetaLoading(true);

					const response = await axios
						.get(`${process.env.REACT_APP_OUTSETA_BASE_URL}/profile`, {
							headers: {
								Authorization: `Bearer ${outsetaToken}`,
								'Content-Type': 'application/json',
							},
						})
						.catch((error) => {
							console.log('error', error);
						});

					if (response && response.data) {
						console.log('profileData', response.data);
						setProfileData(response.data); // Store profile data in state
						console.log('profileData State', profileData);

						dispatch(loginAction({ email: response.data.Email }) as any)
							.unwrap()
							.then(() => {
								dispatch(fetchProfileAction() as any)
									.unwrap()
									.then((res: any) => {
										setIsOutsetaLoading(false);
										dispatch(getCompanies(res));
										navigate('/');
										window.location.reload();
									});
							})
							.catch(() => {
								setIsOutsetaLoading(false);

								navigate('/login');
							});
					} else {
					 console.error('No profile data found');
					setIsOutsetaLoading(false);
					 window.location.href = '/login';
					}
					} catch (error) {
					 console.error('Error fetching profile data:', error);
					 setIsLoggedIn(false);
					setIsOutsetaLoading(false);
					 window.location.href = '/login';
					}
			} else {
				setIsLoggedIn(true);
				setIsOutsetaLoading(false);
			}
		};
		console.log('isOutsetaLoading', isOutsetaLoading);
		fetchProfileData(); // Call the async function
	}, []);

	useEffect(() => {
		if (userData) {
			dispatch(fetchClosingDateAction({}));
		}
	}, [selectedCompanyDetails, userData]);

	// Function to check if popup should be shown
	const checkShouldShowReviewPopup = (userData: any): boolean => {
		try {
			// Check if user has already given review
			const hasGivenReview = userData?.hasGivenReview ?? false;
			if (hasGivenReview) {
				return false;
			}

			// Check if today is the 1st of the month (US timezone)
			const today = dayjs().tz(TIMEZONE);
			const isFirstOfMonth = today.date() === 1;

			if (!isFirstOfMonth) {
				return false;
			}

			// Check if popup was already shown this month
			const lastShownDate = userData?.lastReviewPopupShownDate;
			if (!lastShownDate) {
				// Never shown before, show it
				return true;
			}

			// Check if last shown date is in a different month/year
			const currentYM = today.format("YYYY-MM");
			const lastShownYM = dayjs(lastShownDate).format("YYYY-MM"); // no tz here!

			// Show only if not shown this month
			return lastShownYM !== currentYM;
		} catch (error) {
			console.error('Error checking review popup:', error);
			return false;
		}
	};

	// Check if review popup should be shown
	useEffect(() => {
		if (!isLoading && userData) {
			const shouldShowPopup = checkShouldShowReviewPopup(userData);
			setIsReviewPopupOpen(shouldShowPopup);
		}
	}, [isLoading, userData]);

	// Handle "Write a review" button click
	const handleWriteReview = async () => {
		if (isReviewActionLoading) return; // Prevent multiple clicks

		try {
			setIsReviewActionLoading(true);

			// Open G2 review page in new tab
			window.open(
				'https://www.g2.com/sellers/costallocation-pro#reviews',
				'_blank'
			);

			// Get today's date in YYYY-MM-DD format
			const today = dayjs().tz(TIMEZONE).format('YYYY-MM-DD');

			// Update review status in backend
			dispatch(
				updateReviewStatusAction({
					hasGivenReview: true,
					lastReviewPopupShownDate: today,
				}) as any
			)
				.unwrap()
				.then(() => {
					// Refetch profile to update state
					dispatch(fetchProfileAction() as any);
					setIsReviewActionLoading(false);
					setIsReviewPopupOpen(false);
				})
				.catch((error: any) => {
					console.error('Error updating review status:', error);
					setIsReviewActionLoading(false);
					// Still close popup even if API call fails
					setIsReviewPopupOpen(false);
				});
		} catch (error) {
			console.error('Error handling write review:', error);
			setIsReviewActionLoading(false);
			setIsReviewPopupOpen(false);
		}
	};

	// Handle "No thanks" click
	const handleDismissReview = async () => {
		if (isReviewActionLoading) return; // Prevent multiple clicks

		try {
			setIsReviewActionLoading(true);

			// Get today's date in YYYY-MM-DD format
			const today = dayjs().tz(TIMEZONE).format('YYYY-MM-DD');

			// Update last shown date in backend (but don't set hasGivenReview to true)
			dispatch(
				updateReviewStatusAction({
					lastReviewPopupShownDate: today,
				}) as any
			)
				.unwrap()
				.then(() => {
					// Refetch profile to update state
					dispatch(fetchProfileAction() as any);
					setIsReviewActionLoading(false);
					setIsReviewPopupOpen(false);
				})
				.catch((error: any) => {
					console.error('Error updating review status:', error);
					setIsReviewActionLoading(false);
					// Still close popup even if API call fails
					setIsReviewPopupOpen(false);
				});
		} catch (error) {
			console.error('Error handling dismiss review:', error);
			setIsReviewActionLoading(false);
			setIsReviewPopupOpen(false);
		}
	};

	useIdleRedirect();

	// JSX
	return !isLoading ? (
		isLoggedIn ? (
			// TutorialProvider wraps the whole shell — the payroll tutorial walks
			// across three routes, so its state has to live above <Outlet />.
			<TutorialProvider>
			<div className={styles['global-layout']} style={{ overflowY: 'hidden' }}>
				<div className={styles['global-layout__wrapper']}>
					<div className={styles['global-layout__header']}>
						<Header />
					</div>
					<div className={styles['global-layout__body']}>
						<div
							className={
								styles[
								`global-layout__body--sidebar-${isExpanded ? 'hide' : 'show'
								}`
								]
							}
						>
							<Sidebar
								items={finalMenuItems}
								// items={pageMenuItems}
								isGetSupportButton={true}
								handleSidebar={handleSidebar}
								selectedSidebar={selectedSidebar}
								collapsed={sidebarCollapsed}
								onCollapse={() => dispatch(setSidebarCollapsed(!sidebarCollapsed))}
								showChatNav={hasAiAssistantAccess}
							/>
						</div>

                         <div
								className={`${
									styles[
									`global-layout__body--body-${
										isExpanded
											? 'widthFull'
											: sidebarCollapsed
												? 'widthFixedCollapsed'
												: 'widthFixed'
									}`
									]
								} ${pathname === '/chat' ? styles['global-layout__body--chat'] : ''}`}
							>
							<Outlet />
						</div>
					</div>
				</div>
				{isSideDrawerOpen && (
					<SideDrawerWrapper
						isOpen={drawerAnimation}
						removeDrawerFromDom={removeDrawerFromDom}
						closeDrawerByAnimation={closeDrawerByAnimation}
						headerTitle={drawerInfo.drawerTitle}
						position="bottom"
						width="full"
					>
						<SettingsLayout
							filteredMenuItems={filteredMenuItems}
							onSideBarChange={sideBarChangeHandler}
							selectedSidebar={selectedSidebarSettings}
						>
							<SettingsBody
								selectedSidebar={selectedSidebarSettings}
								setSelectedSidebar={setSelectedSidebarSettings}
								closeDrawerByAnimation={closeDrawerByAnimation}
								openDrawerHandler={openDrawerHandler}
							/>
						</SettingsLayout>
					</SideDrawerWrapper>
				)}
				<ReviewPopup
					isModalOpen={isReviewPopupOpen}
					handleCancel={handleDismissReview}
					handleWriteReview={handleWriteReview}
					isLoading={isReviewActionLoading}
				/>
				{/* The AI assistant now lives only on the dedicated /chat page
				    (ChatSurface renders <ChatBubble embedded />). The floating
				    bubble that used to appear on every other page has been
				    removed — reach the assistant via the sidebar's "New chat". */}
				<TutorialTour />
			</div>
			</TutorialProvider>
		) : (
			<div className={styles['spin-container']}>
				<Spin
					indicator={
						<LoadingOutlined style={{ fontSize: 48, color: '#344735' }} spin />
					}
				/>
				<p className={styles['spin-message']}>
					Just a moment! We&apos;re fetching your data from the server.
				</p>
			</div>
		)
	) : (
		<div className={styles['spin-container']}>
			<Spin
				indicator={
					<LoadingOutlined style={{ fontSize: 48, color: '#344735' }} spin />
				}
			/>
			<p className={styles['spin-message']}>
				Just a moment! We&apos;re fetching your data from the server.
			</p>
		</div>
	);
};

export default GlobalLayout;
